import React, { useState, ReactNode, useEffect } from "react";

interface ReadMoreProps {
  children: ReactNode;
  mobileMaxCharacterCount?: number; // New prop for mobile character count
  desktopMaxCharacterCount?: number; // New prop for desktop character count
}

const ReadMore: React.FC<ReadMoreProps> = ({
  children,
  mobileMaxCharacterCount = 150,
  desktopMaxCharacterCount = 348,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [maxCharacterCount, setMaxCharacterCount] = useState(mobileMaxCharacterCount);

  const toggleReadMore = () => {
    setIsExpanded(!isExpanded);
  };

  // Utility function to extract plain text for character counting
  const getTextContent = (node: ReactNode): string => {
    if (node == null) return ""; // Handle null or undefined nodes
    if (typeof node === "string") return node;
    if (Array.isArray(node)) return node.map(getTextContent).join("");
    if (typeof node === "object" && "props" in node) {
      return getTextContent(node.props.children);
    }
    return "";
  };

  const textContent = getTextContent(children);
  const shouldTruncate = textContent.length > maxCharacterCount;

  // Effect to update character count based on screen width
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) { // Mobile breakpoint (can adjust as needed)
        setMaxCharacterCount(mobileMaxCharacterCount);
      } else {
        setMaxCharacterCount(desktopMaxCharacterCount);
      }
    };

    // Initial check
    handleResize();

    // Add event listener for resize
    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, [mobileMaxCharacterCount, desktopMaxCharacterCount]);

  return (
    <div className="">
      {isExpanded || !shouldTruncate ? (
        <>
          {children}
          <div className="flex justify-end">
            <span
              onClick={toggleReadMore}
              className="text-sm text-blue-500 hover:text-blue-700 cursor-pointer"
            >
              Read Less
            </span>
          </div>
        </>
      ) : (
        <>
          {textContent.slice(0, maxCharacterCount)}
          <span>...</span>
          <span
            onClick={toggleReadMore}
            className="text-sm text-blue-500 hover:text-blue-700 cursor-pointer ml-2 inline"
          >
            Read More
          </span>
        </>
      )}
    </div>
  );
};

export default ReadMore;
